import { QueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/api/query/querykeys";

/**
 * Invalidates all queries associated with a task change.
 * Ensures instant synchronization across Gantt, Kanban, Tasks Table,
 * Project Details, Projects List, Portfolio Dashboard, and Budget views.
 */
export async function invalidateTaskRelatedQueries(
  queryClient: QueryClient,
  params?: { projectId?: string; taskId?: string }
): Promise<void> {
  const { projectId, taskId } = params || {};

  const invalidations: Promise<void>[] = [
    // Task-level and project-task lists
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.myTasks] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allKanbanColumn] }),

    // Project aggregated data
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.portfolioDashboard] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardPortfolioProgress] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardProjectPerformance] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardStatusSummary] }),

    // Budget & Performance
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetPerformance] }),
  ];

  if (projectId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetOverview, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projectSummary, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projectPerformance, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projectAlerts, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projectHighlights, projectId] })
    );
  }

  if (taskId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, taskId] })
    );
  }

  await Promise.allSettled(invalidations);
}

/**
 * Invalidates all queries associated with project modifications.
 */
export async function invalidateProjectRelatedQueries(
  queryClient: QueryClient,
  params?: { projectId?: string }
): Promise<void> {
  const { projectId } = params || {};

  const invalidations: Promise<void>[] = [
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.portfolioDashboard] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardPortfolioProgress] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardProjectPerformance] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dashboardStatusSummary] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetPerformance] }),
  ];

  if (projectId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ProjectMember, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetOverview, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] })
    );
  }

  await Promise.allSettled(invalidations);
}

/**
 * Invalidates all queries associated with cost (labor and non-labor) changes.
 */
export async function invalidateCostRelatedQueries(
  queryClient: QueryClient,
  params?: { projectId?: string }
): Promise<void> {
  const { projectId } = params || {};

  const invalidations: Promise<void>[] = [
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetPerformance] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] }),
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.portfolioDashboard] }),
  ];

  if (projectId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.laborCosts, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.nonLaborCosts, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.budgetOverview, projectId] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] })
    );
  }

  await Promise.allSettled(invalidations);
}
